import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Box,
  Snackbar,
  Alert,
  InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FaArrowLeft, FaSearch, FaPlus } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { cvManagementApi } from '../../../services/api';
import { Container, Header, HeaderLeft, BackButton, Title, Content, MainSection, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, DegreesGrid, DegreeCard, DegreeCardContent, DegreeTitle, OrderBadge, DegreeDetailsGrid, ActionButtons, ItalicText } from './styles';
import { COLORS } from '../../../constants/radar';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import { useAlert } from '../../../contexts/AlertContext';

const EducationDegree = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [degrees, setDegrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentDegree, setCurrentDegree] = useState({ name: '', order: 0 });
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    fetchEducationDegrees();
  }, []);

  const fetchEducationDegrees = async () => {
    try {
      setLoading(true);
      const data = await cvManagementApi.getAllEducationDegrees();
      setDegrees(data);
    } catch (err) {
      showAlert('Failed to load education degrees.', 'error', err);
      console.error('Error fetching education degrees:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (degree = null) => {
    if (degree) {
      setCurrentDegree(degree);
      setIsEditing(true);
    } else {
      setCurrentDegree({ name: '', order: degrees.length + 1 });
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (degree) => {
    setCurrentDegree(degree);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentDegree({
      ...currentDegree,
      [name]: name === 'order' ? parseInt(value, 10) || 0 : value
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (isEditing) {
        await cvManagementApi.updateEducationDegree(currentDegree.id, currentDegree);
        showAlert('Education degree updated successfully', 'success');
      } else {
        await cvManagementApi.createEducationDegree(currentDegree);
        showAlert('Education degree created successfully', 'success');
      }
      handleCloseDialog();
      await fetchEducationDegrees();
    } catch (err) {
      showAlert(err.message || `Failed to ${isEditing ? 'update' : 'create'} education degree. Please try again.`, 'error', err);
      console.error(`Error ${isEditing ? 'updating' : 'creating'} education degree:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await cvManagementApi.deleteEducationDegree(currentDegree.id);
      showAlert('Education degree deleted successfully', 'success');
      handleCloseDeleteDialog();
      await fetchEducationDegrees();
    } catch (err) {
      showAlert(err.message || 'Failed to delete education degree. Please try again.', 'error', err);
      console.error('Error deleting education degree:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDegrees = degrees.filter(degree => 
    degree.name.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  if (loading && degrees.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/cv-configuration')}>
            <FaArrowLeft /> Back to CV Configuration
          </BackButton>
          <Title>Education Degree Configuration</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Degrees"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />
            </Box>

            <HeaderButton 
              onClick={() => handleOpenDialog()}
              sx={{
                minWidth: 'auto',
                height: '32px',
                padding: '0 16px',
                backgroundColor: '#2563eb',
                color: 'white',
                alignSelf: 'flex-start',
                '&:hover': {
                  backgroundColor: '#1d4ed8',
                }
              }}
            >
              <FaPlus style={{ marginRight: '8px' }} /> Add Degree
            </HeaderButton>
          </Box>
        </ControlsContainer>

        <MainContainer>
          <div style={{ 
            color: '#64748B', 
            fontSize: '14px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            <span style={{ fontWeight: 600 }}>
              {filteredDegrees.length}
            </span>
            {filteredDegrees.length === 1 ? 'degree' : 'degrees'} found
          </div>

          {filteredDegrees.length === 0 ? (
            <EmptyState
              title="No education degrees found"
              description={searchTerm ? 
                "No education degrees match your search criteria. Try adjusting your search term." : 
                "No education degrees have been added yet. Click the '+ Add Degree' button to add your first degree."}
            />
          ) : (
            <DegreesGrid>
              {filteredDegrees.map((degree) => (
                <DegreeCard key={degree.id}>
                  <DegreeCardContent>
                    <DegreeTitle>
                      {degree.name}
                      <OrderBadge>
                        Order: {degree.order}
                      </OrderBadge>
                    </DegreeTitle>
                    <ActionButtons>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDialog(degree)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDeleteDialog(degree)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ActionButtons>
                  </DegreeCardContent>
                </DegreeCard>
              ))}
            </DegreesGrid>
          )}
        </MainContainer>
      </Content>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Education Degree' : 'Add Education Degree'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Degree Name"
            type="text"
            fullWidth
            variant="outlined"
            value={currentDegree.name}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="order"
            label="Display Order"
            type="number"
            fullWidth
            variant="outlined"
            value={currentDegree.order}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!currentDegree.name.trim()}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the education degree "{currentDegree.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EducationDegree; 